import {
  contactsRetrieveOptions,
  customFieldsListOptions,
  EntityTypeEnum,
  Type83fEnum
} from '@crm/api'
import { CustomField, PhoneInput } from '@crm/components'
import { Text } from '@crm/components'
import { AddExistingContactModalForm } from '@crm/components/InfoTabs/Tabs'
import { defaultNonPagination } from '@crm/utils'
import { useLocales } from '@shared/locales'

import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Switch,
  Tooltip
} from 'antd'
import { Add, Trash } from 'iconsax-react'
import { useEffect, useState } from 'react'

type Props = {
  form: FormInstance
  onSaveExistingContact?: () => void
  isEdit?: boolean
}

export const GenericDetails = ({
  form,
  onSaveExistingContact,
  isEdit
}: Props) => {
  const [isOpenModalAddExistingContact, setIsOpenModalAddExistingContact] =
    useState(false)
  const [contactId, setContactId] = useState<number | undefined>(undefined)

  const { t } = useLocales()

  useEffect(() => {
    if (!isEdit) {
      const existing = form.getFieldValue(['contact', 'phone_numbers'])
      if (!existing || existing.length === 0) {
        form.setFieldValue(
          ['contact', 'phone_numbers'],
          [{ number: '', is_primary: true }]
        )
      }
    }
  }, [])

  const { data: contactData } = useQuery({
    ...contactsRetrieveOptions({
      path: { id: Number(contactId) }
    }),
    enabled: !!contactId
  })

  const { data } = useQuery(
    customFieldsListOptions({
      query: {
        entity_type: [EntityTypeEnum.CONTACT],
        ...defaultNonPagination
      }
    })
  )

  const availableCustomFields = data?.results?.filter((e) => !e.deleted_at)

  const { filteredCustomFields, checkboxCustomFields } = Object.groupBy(
    availableCustomFields || [],
    (e) =>
      e.type === Type83fEnum.BOOLEAN
        ? 'checkboxCustomFields'
        : 'filteredCustomFields'
  )

  const onChangePrimaryPhone = (checked: boolean, currentIndex: number) => {
    if (checked) {
      const phoneNumbers =
        form.getFieldValue(['contact', 'phone_numbers']) || []

      phoneNumbers.forEach((_: unknown, index: number) => {
        if (index !== currentIndex) {
          form.setFieldValue(
            ['contact', 'phone_numbers', index, 'is_primary'],
            false
          )
        }
      })
    }
  }

  const onToggleAddExistingContact = () =>
    setIsOpenModalAddExistingContact((prev) => !prev)

  const onAddExistingContact = async (id: number) => {
    setContactId(id)
    onToggleAddExistingContact()
    onSaveExistingContact?.()
  }

  useEffect(() => {
    if (contactData) {
      form.setFieldsValue({
        contact: {
          id: contactData?.id,
          first_name: contactData.first_name,
          last_name: contactData.last_name,
          email: contactData?.email,
          linkedIn: contactData?.linkedin,
          phone_numbers: contactData?.phone_numbers
        }
      })
    }
  }, [contactData, form])

  const isFormDisabled = !!contactId || isEdit

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Flex align="center" justify="space-between">
            <Text size={16} weight={500}>
              {t('contacts.contact_data')}
            </Text>

            <Button
              type="primary"
              icon={<Add size={20} />}
              onClick={onToggleAddExistingContact}
              hidden={isEdit}
            >
              {t('contacts.add_existing_contact')}
            </Button>
          </Flex>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['contact', 'last_name']}
            label={t('contacts.last_name')}
            rules={[{ required: true }]}
          >
            <Input
              placeholder={t('contacts.last_name')}
              disabled={isFormDisabled}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['contact', 'first_name']}
            label={t('contacts.first_name')}
            rules={[{ required: true }]}
          >
            <Input
              placeholder={t('contacts.first_name')}
              disabled={isFormDisabled}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={['contact', 'email']}
            label={t('contacts.email')}
            rules={[{ type: 'email' }]}
          >
            <Input
              type="email"
              placeholder={t('contacts.email')}
              disabled={isFormDisabled}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.List
            name={['contact', 'phone_numbers']}
            rules={[
              {
                validator: async (_, phoneNumbers) => {
                  if (!phoneNumbers || phoneNumbers.length < 1) {
                    return Promise.reject(
                      new Error(t('contacts.phone_number_required'))
                    )
                  }
                }
              }
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <Flex vertical gap={8}>
                <Flex align="center" justify="space-between" gap={8}>
                  <Text>
                    <span style={{ color: '#ff4d4f', marginInlineEnd: 4 }}>
                      *
                    </span>

                    {t('contacts.phone_numbers')}
                  </Text>
                  <Button
                    variant="dashed"
                    color="primary"
                    onClick={() => add()}
                    disabled={isFormDisabled}
                    icon={<Add />}
                  >
                    {t('contacts.add_phone_number')}
                  </Button>
                </Flex>

                <Row gutter={[16, 16]} justify="space-between">
                  {fields?.map(({ key, name, ...restField }) => (
                    <Col span={12} key={key}>
                      <Flex gap={8} align="center">
                        <Flex className="full-width" style={{ width: '100%' }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'number']}
                            style={{ width: '100%' }}
                            rules={[
                              {
                                required: true,
                                message: t(
                                  'account_settings.this_field_is_required'
                                )
                              }
                            ]}
                          >
                            <PhoneInput
                              placeholder={t('contacts.phone_number')}
                              disabled={isFormDisabled}
                            />
                          </Form.Item>
                        </Flex>

                        <Tooltip title={t('contacts.set_primary_number')}>
                          <Form.Item
                            {...restField}
                            name={[name, 'is_primary']}
                            valuePropName="checked"
                            initialValue={name === 0}
                          >
                            <Switch
                              disabled={isFormDisabled}
                              onChange={(checked) =>
                                onChangePrimaryPhone(checked, name)
                              }
                            />
                          </Form.Item>
                        </Tooltip>

                        <Form.Item>
                          <Button
                            danger
                            icon={<Trash size={16} />}
                            onClick={() => remove(name)}
                            disabled={isFormDisabled}
                          />
                        </Form.Item>
                      </Flex>
                    </Col>
                  ))}

                  <Col span={24}>
                    <Text color="colorError">{errors?.join?.(', ')}</Text>
                  </Col>
                </Row>
              </Flex>
            )}
          </Form.List>
        </Col>

        {((filteredCustomFields?.length ?? 0) > 0 ||
          (checkboxCustomFields?.length ?? 0) > 0) && (
          <>
            <Divider />

            <Col span={24}>
              <Text size={16} weight={500}>
                {t('nomenclature.custom_fields')}
              </Text>
            </Col>

            {filteredCustomFields?.map((item) => (
              <Col span={12} key={item.id}>
                <CustomField
                  name={['contact', 'custom_field_values']}
                  disabled={!!contactId}
                  {...item}
                />
              </Col>
            ))}

            {checkboxCustomFields?.map((item) => (
              <Col span={24} key={item.id}>
                <CustomField
                  name={['contact', 'custom_field_values']}
                  disabled={!!contactId}
                  {...item}
                />
              </Col>
            ))}
          </>
        )}
      </Row>

      <AddExistingContactModalForm
        open={isOpenModalAddExistingContact}
        onCancel={onToggleAddExistingContact}
        onSubmit={async (id) => await onAddExistingContact(id)}
        showFooter={false}
      />
    </>
  )
}
